import { TrackingExplorer } from "@/components/marketing/TrackingExplorer";
import { YouTubeFacade } from "@/components/marketing/YouTubeFacade";
import { SectionGroups } from "@/components/marketing/SectionLayout";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { GooglePreferredSource } from "@/components/marketing/GooglePreferredSource";
import logoBleu from "@/assets/logo-bleu.png.asset.json";
import { breadcrumbJsonLd } from "@/content/schema";
import { PUBLISHED_AT, MODIFIED_AT, formatResearchDate } from "@/content/researchDates";
import headlineImg from "@/assets/home-services-conversion-tracking-2026-headline-v2.png.asset.json";
import byTradeImg from "@/assets/home-services-conversion-tracking-by-trade-2026-v2.png.asset.json";
import { ORG_ID, PERSON_ID, SITE, abs } from "@/content/siteConfig";

const TITLE = "Conversion Tracking Report 2026: 9,560 Home Services Websites Measured";
// Shorter document/social title; Article JSON-LD headline keeps TITLE.
const SEO_TITLE = "Conversion Tracking Report 2026: 9,560 Home Services Sites";
const DESCRIPTION =
  "We loaded 9,560 Arizona, California and Nevada home services websites in a real browser. 70% had no conversion tracking at all.";
const PATH = "/research/conversion-tracking-report";
const URL = abs(PATH);


// 2026 edition, scanned through 5 October 2026. Blank (null) rates are deliberate:
// fewer than 30 confirmed advertisers, so no percentage is published. Never fill them in.
type TradeRow = {
  id: string;
  trade: string;
  industry?: string;
  scanned: number;
  noTracking: string;
  ads: number;
  adsNoTracking: string | null;
};
const TRADE_ROWS: TradeRow[] = [
  { id: "electricians", trade: "Electricians", scanned: 326, noTracking: "274 (84.0%)", ads: 50, adsNoTracking: "28 (56.0%)" },
  { id: "painting", trade: "Painters", industry: "painting", scanned: 233, noTracking: "190 (81.5%)", ads: 28, adsNoTracking: null },
  { id: "concrete", trade: "Concrete contractors", scanned: 190, noTracking: "154 (81.1%)", ads: 20, adsNoTracking: null },
  { id: "landscaping", trade: "Landscapers", industry: "landscaping", scanned: 239, noTracking: "184 (77.0%)", ads: 19, adsNoTracking: null },
  { id: "pest-control", trade: "Pest control", scanned: 213, noTracking: "163 (76.5%)", ads: 39, adsNoTracking: "16 (41.0%)" },
  { id: "cleaning", trade: "House cleaning", industry: "cleaning", scanned: 165, noTracking: "124 (75.2%)", ads: 31, adsNoTracking: "11 (35.5%)" },
  { id: "roofing", trade: "Roofers", industry: "roofing", scanned: 297, noTracking: "219 (73.7%)", ads: 62, adsNoTracking: "21 (33.9%)" },
  { id: "plumbing", trade: "Plumbers", scanned: 251, noTracking: "183 (72.9%)", ads: 55, adsNoTracking: "23 (41.8%)" },
  { id: "remodeling", trade: "Remodelers", scanned: 195, noTracking: "134 (68.7%)", ads: 46, adsNoTracking: "9 (19.6%)" },
  { id: "windows", trade: "Window companies", scanned: 172, noTracking: "113 (65.7%)", ads: 42, adsNoTracking: "7 (16.7%)" },
  { id: "hvac", trade: "HVAC", scanned: 230, noTracking: "138 (60.0%)", ads: 68, adsNoTracking: "18 (26.5%)" },
  { id: "pool-service", trade: "Pool service", industry: "pool-service", scanned: 284, noTracking: "152 (53.5%)", ads: 46, adsNoTracking: "11 (23.9%)" },
];

const DATASET_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Dataset",
  name: "Conversion Tracking Coverage Across Southwest US Home Services Websites, 2026 Edition",
  description:
    "An original study of conversion tracking installed on 9,560 home services business websites in Arizona, California and Nevada, each loaded in a real browser rather than surveyed. Includes a per-trade breakdown across twelve trades.",
  url: "https://berkhospitality.com/research/conversion-tracking-report",
  datePublished: "2026-10-05",
  creator: { "@type": "Organization", name: "Berk Hospitality Group", url: "https://berkhospitality.com" },
  license: "https://berkhospitality.com/research/conversion-tracking-report",
  isAccessibleForFree: true,
  measurementTechnique: "Headless browser page render with tag detection",
  variableMeasured: [
    "Presence of any conversion tracking",
    "Google Analytics 4 installation",
    "Google Ads conversion tag installation",
    "Google Tag Manager installation",
    "Active paid advertising",
  ],
  spatialCoverage: [
    { "@type": "State", name: "Arizona" },
    { "@type": "State", name: "California" },
    { "@type": "State", name: "Nevada" },
  ],
  temporalCoverage: "2026",
};

export const Route = createFileRoute("/research/conversion-tracking-report")({
  head: () => ({
    meta: [
      { title: SEO_TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: SEO_TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { property: "og:image", content: abs(headlineImg.url) },
      { property: "og:image:width", content: "1440" },
      { property: "og:image:height", content: "677" },

      {
        property: "og:image:alt",
        content:
          "70.1% of 9,560 home services websites had no conversion tracking, and 27.4% of the 1,807 businesses running ads had none",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: abs(headlineImg.url) },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Research", path: "/research" },
          { name: "The Conversion Tracking Report", path: PATH },
        ]),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESCRIPTION,
          image: {
            "@type": "ImageObject",
            url: abs(headlineImg.url),
            width: 1440,
            height: 677,
          },
          datePublished: PUBLISHED_AT,
          dateModified: MODIFIED_AT,
          author: { "@id": PERSON_ID },
          publisher: {
            "@type": "Organization",
            "@id": ORG_ID,
            name: SITE.name,
            url: SITE.origin,
            logo: {
              "@type": "ImageObject",
              url: abs(`${logoBleu.url}`),
            },
          },
          articleSection: "Research",
          mainEntityOfPage: { "@type": "WebPage", "@id": URL },
          url: URL,
        }),
      },
      { type: "application/ld+json", children: JSON.stringify(DATASET_JSON_LD) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "VideoObject",
          name: "We loaded 9,560 home services websites. 27.4% of advertisers had no conversion tracking.",
          description:
            "Original research from Berk Hospitality Group, 2026 edition. 9,560 home services websites in Arizona, California and Nevada, each loaded in a real browser.",
          thumbnailUrl: "https://i.ytimg.com/vi/Diot2l_EJrw/maxresdefault.jpg",
          uploadDate: "2026-10-22",
          duration: "PT1M13S",
          embedUrl: "https://www.youtube.com/embed/Diot2l_EJrw",
          publisher: { "@type": "Organization", name: SITE.name },
        }),
      },
    ],
  }),
  component: ConversionTrackingReport,
});

function ConversionTrackingReport() {
  return (
    <SiteShell>
      <div className="wrap" style={{ paddingTop: 120, paddingBottom: 120 }}>
        <div className="reading-layout" style={{ maxWidth: 720, margin: "0 auto" }}>
          <Link
            to="/research"
            className="mono"
            style={{
              fontSize: 11,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "rgba(0,47,167,0.6)",
            }}
          >
            ← Research
          </Link>
          <div
            className="mono mt-8"
            style={{
              fontSize: 11,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "rgba(0,47,167,0.9)",
            }}
          >
            Original research · By{" "}
            <Link to="/about" style={{ color: "inherit", textDecoration: "underline" }}>
              Jett Berk
            </Link>{" "}
            ·{" "}
            {new Date(MODIFIED_AT).toLocaleDateString("en-US", {
              timeZone: "America/Phoenix",
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </div>
          <h1
            className="mt-4"
            style={{ fontSize: "clamp(32px, 5vw, 54px)", lineHeight: 1.06, letterSpacing: "-0.02em" }}
          >
            The Conversion Tracking Report
          </h1>
          <div
            className="mono mt-4 report-edition-dates"
            style={{ fontSize: 12, letterSpacing: "0.08em", color: "rgba(0,0,0,0.55)" }}
          >
            <span>2026 edition</span><span>n = 9,560</span><span>scanned through 5 October 2026</span>
            <span className="report-date"><b>Published</b> <time dateTime={PUBLISHED_AT}>{formatResearchDate(PUBLISHED_AT)}</time></span>
            <span className="report-date"><b>Updated</b> <time dateTime={MODIFIED_AT}>{formatResearchDate(MODIFIED_AT)}</time></span>
          </div>
          <p className="prose-lead mt-6" style={{ fontSize: 19, lineHeight: 1.55, color: "rgba(0,0,0,0.72)" }}>
            {DESCRIPTION}
          </p>

          <YouTubeFacade
            id="Diot2l_EJrw"
            title="We loaded 9,560 home services websites. 27.4% of advertisers had no conversion tracking."
            alt="Video: We loaded 9,560 home services websites. 27.4% of advertisers had no conversion tracking."
          />

          <SectionGroups className="blog-content section-groups mt-12">
            <h2>How many home services businesses have no conversion tracking?</h2>
            <figure>
              <a href={headlineImg.url} target="_blank" rel="noopener">
              <img
                src={headlineImg.url}
                alt="70.1% of 9,560 home services websites had no conversion tracking, and 27.4% of the 1,807 businesses running ads had none"
                width={1440}
                height={677}
                decoding="async"
              />
              </a>
              <figcaption>
                9,560 home services websites in Arizona, California and Nevada, scanned to 5
                October 2026.
              </figcaption>
            </figure>
            <p>
              This edition covers 9,560 home services businesses in Arizona, California and Nevada.
              It is a deep regional sample, not a national one.
            </p>
            <p>
              Most reporting on ad measurement is based on surveys: asking marketers what they think
              is installed. We did it the other way round. As of 5 October 2026 we had loaded 9,560
              home-services business websites in a real browser, looked at what was actually running,
              and cross-referenced which of those businesses were confirmed to be buying ads.
            </p>
            <p>
              <strong>6,704 of the 9,560 (70.1%) had no conversion tracking at all</strong>,
              whether they were advertising or not.
            </p>

            <TrackingExplorer />

            <h2>How many businesses are buying ads with nothing measuring the result?</h2>
            <p>
              <strong>
                Of the businesses we could confirm were actively running ads, 27.4% had no conversion
                tracking of any kind.
              </strong>{" "}
              Not misconfigured. Not partially firing. Nothing on the page that could record a lead.
            </p>
            <p>
              That is 27.4% of advertisers spending money with no mechanism to know what
              it produced.
            </p>
            <ul>
              <li>
                <strong>9,560</strong> home-services business websites rendered and inspected
              </li>
              <li>
                <strong>1,807</strong> confirmed to be running ads at the time of the scan
              </li>
              <li>
                <strong>495 of those 1,807 (27.4%) had no conversion tracking</strong>
              </li>
              <li>
                <strong>6,704 of all 9,560 (70.1%) had no conversion tracking at all</strong>, whether
                they were advertising or not
              </li>
              <li>
                <strong>2,228 (23.3%)</strong> of all sites had GA4 installed
              </li>
              <li>
                <strong>1,629 (17.0%)</strong> had a Google Ads conversion tag
              </li>
              <li>
                <strong>1,291 (13.5%)</strong> had Google Tag Manager
              </li>
            </ul>

            <h2>Which trades are worst at measuring their advertising?</h2>
            <figure>
              <a href={byTradeImg.url} target="_blank" rel="noopener">
              <img
                src={byTradeImg.url}
                alt="Bar chart of home services websites with no conversion tracking by trade, from electricians at 84.0% to pool service at 53.5%"
                width={1440}
                height={802}
                loading="lazy"
                decoding="async"
              />
              </a>
              <figcaption>
                Share of sites scanned with no conversion tracking, by trade. Full figures in the
                table below.
              </figcaption>
            </figure>
            <p>
              The rate of blind advertisers is not evenly distributed. Among electricians running
              ads, it is worse than a coin flip.
            </p>

            <div
              style={{
                overflowX: "auto",
                WebkitOverflowScrolling: "touch",
                margin: "1.6em 0",
                border: "1px solid rgba(0,47,167,0.16)",
                borderRadius: 6,
              }}
            >
              <table
                style={{
                  width: "100%",
                  minWidth: 720,
                  borderCollapse: "collapse",
                  fontSize: 15,
                }}
              >
                <caption
                  className="mono"
                  style={{
                    captionSide: "top",
                    textAlign: "left",
                    padding: "14px 16px",
                    fontSize: 11,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: "rgba(0,0,0,0.55)",
                  }}
                >
                  Conversion tracking by trade, 2026 edition
                </caption>
                <thead>
                  <tr style={{ background: "#fbfaf6" }}>
                    <th scope="col" style={thStyle}>Trade</th>
                    <th scope="col" style={{ ...thStyle, textAlign: "right" }}>Sites scanned</th>
                    <th scope="col" style={{ ...thStyle, textAlign: "right" }}>No conversion tracking</th>
                    <th scope="col" style={{ ...thStyle, textAlign: "right" }}>Running ads</th>
                    <th scope="col" style={{ ...thStyle, textAlign: "right" }}>Advertising with no tracking</th>
                  </tr>
                </thead>
                <tbody>
                  {TRADE_ROWS.map((r) => (
                    <tr key={r.id} id={r.id}>
                      <th scope="row" style={{ ...tdStyle, fontWeight: 500, textAlign: "left" }}>
                        {r.industry ? (
                          <Link to="/industries/$industry" params={{ industry: r.industry }}>
                            {r.trade}
                          </Link>
                        ) : (
                          r.trade
                        )}
                      </th>
                      <td style={{ ...tdStyle, textAlign: "right" }}>{r.scanned}</td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>{r.noTracking}</td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>{r.ads}</td>
                      <td style={{ ...tdStyle, textAlign: "right" }}>{r.adsNoTracking ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p>
              Where a trade had fewer than 30 confirmed advertisers we show the count and leave the
              percentage blank. A rate built on nineteen businesses is not a rate.
            </p>
            <p>
              Read the &quot;running ads&quot; column before drawing conclusions from any single
              row. A trade with a few dozen advertisers gives a useful signal, not a precise national
              figure, and a difference of a point or two at that sample size is noise. The pattern
              that survives the small numbers is the spread: the gap between the top of this table
              and the bottom is real, and it is large.
            </p>

            <h2>What counts as conversion tracking in this study?</h2>
            <p>
              This is the part most reports skip, so we will be specific about what the number can
              and cannot support.
            </p>
            <p>
              <strong>
                We counted a site as having no tracking when a real browser loaded the page and found
                no Google Tag Manager container, no GA4, no Google Ads conversion tag, no Meta pixel,
                no Microsoft UET, and no call-tracking script.
              </strong>{" "}
              A rendered browser session: not a raw HTML fetch, which misses anything injected by a
              tag manager at runtime.
            </p>
            <p>
              <strong>
                The 27.4% is a ceiling, not an exact figure, and it is almost certainly slightly too
                high.
              </strong>{" "}
              Three reasons:
            </p>
            <ol>
              <li>
                <strong>Server-side tagging is invisible to this method.</strong> A business running a
                server-side container can be fully tracked while a page scan sees nothing. We
                recognize several server-side setups explicitly, but not all of them.
              </li>
              <li>
                <strong>Consent-gated tags may not fire</strong> for an automated visitor that never
                clicks accept.
              </li>
              <li>
                <strong>A site can track leads without any of the above</strong>: a phone number and
                a notepad is a measurement system, just not one you can optimize a campaign against.
              </li>
            </ol>
            <p>
              <strong>Confirmed advertising</strong> means the business had live ads visible in
              Google&apos;s Ads Transparency Center at the time of the scan. The absence of ads there
              is not proof a business isn&apos;t advertising (they may run under a different account
              name, or on a platform it doesn&apos;t cover) so businesses we could not confirm are
              excluded from the 1,807 entirely rather than counted as non-advertisers.
            </p>

            <h2>How did we check 9,560 websites?</h2>
            <ul>
              <li>
                <strong>Sample:</strong> 9,560 home-services businesses, scanned through 5 October 2026, drawn from metropolitan areas
                in Arizona, California and Nevada. The largest concentrations are Phoenix, San Diego,
                Scottsdale, Bakersfield, Palm Springs and Tempe.
              </li>
              <li>
                <strong>Method:</strong> each site was loaded in a real headless browser, allowed to
                execute JavaScript, and its rendered DOM inspected for tracking signals. Detection is
                deterministic pattern-matching, not a model: the same site produces the same verdict
                every time.
              </li>
              <li>
                <strong>Advertising status</strong> was checked against Google&apos;s Ads Transparency
                Center.
              </li>
              <li>
                <strong>This is not a national random sample.</strong> It is a large, systematically
                collected sample of home-services businesses concentrated in three US states, and it should be read
                as that.
              </li>
            </ul>

            <h2>What happens when a business advertises without tracking?</h2>
            <p>
              An advertiser with no conversion tracking is not measuring badly. They are not
              measuring at all. Every decision in that account (which keyword to raise, which
              campaign to cut, whether the whole channel is working) is being made on clicks. That
              is the first thing we rebuild when we take over{" "}
              <Link to={"/services/google-ads" as never}>a Google Ads account</Link>, and it is where every{" "}
              <Link to="/case-studies">engagement we write up</Link> starts.
            </p>
            <p>
              Clicks and jobs are not the same thing, and the gap between them is where the money
              goes. A campaign producing expensive clicks and cheap jobs looks like a failure. A
              campaign producing cheap clicks and no jobs looks like a success. Without conversion
              data, both mistakes are invisible, and the second one can run for years.
            </p>
            <p>The 27.4% figure is the part of the industry that cannot tell those two apart.</p>

            <h2>How does this edition differ from earlier versions?</h2>
            <p>
              When we first published this the sample was 7,218 businesses and the figure was 29%.
              This 2026 edition is a snapshot: every figure on this page describes the 9,560 sites
              scanned through 5 October 2026, and later scans will be published as a new edition. If you want to cite this research, link to this page rather than
              quoting a figure in isolation, so the number a reader sees is the current one.
            </p>

            <GooglePreferredSource
              className="mt-10"
              heading="Get our research in your Google results"
              fallbackText="Get our research in your Google results"
            />

            <p style={{ color: "rgba(0,0,0,0.62)" }}>
              If you would like to know which side of the 27.4% you are on, run our{" "}
              <Link to="/audit">free conversion tracking checker</Link> for an instant look at what
              is firing on your site, or <Link to="/contact">send us your site</Link> directly. No
              charge, no pitch.
            </p>
          </SectionGroups>
        </div>
      </div>
    </SiteShell>
  );
}

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "12px 16px",
  borderBottom: "1px solid rgba(0,47,167,0.22)",
  fontSize: 12.5,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "rgba(0,0,0,0.6)",
  fontWeight: 500,
  whiteSpace: "nowrap",
};

const tdStyle: React.CSSProperties = {
  padding: "11px 16px",
  borderBottom: "1px solid rgba(0,0,0,0.08)",
  color: "rgba(0,0,0,0.82)",
  whiteSpace: "nowrap",
};
